import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import VatBoxPopover from './VatBoxPopover';

const BOX_LABELS = {
    '05': 'Momspliktig försäljning', '06': 'Momspliktiga uttag',
    '07': 'Vinstmarginalbeskattning', '08': 'Hyresinkomster',
    '10': 'Utgående moms 25%', '11': 'Utgående moms 12%', '12': 'Utgående moms 6%',
    '20': 'Inköp varor EU', '21': 'Inköp tjänster EU', '22': 'Inköp tjänster utanför EU',
    '23': 'Inköp varor Sverige (omvänd)', '24': 'Övriga inköp tjänster (omvänd)',
    '30': 'Utgående moms 25% (omvänd)', '31': 'Utgående moms 12% (omvänd)', '32': 'Utgående moms 6% (omvänd)',
    '35': 'Försäljning varor EU', '36': 'Försäljning varor utanför EU',
    '37': 'Mellanman inköp', '38': 'Mellanman försäljning',
    '39': 'Försäljning tjänster EU', '40': 'Övrig försäljning tjänster',
    '41': 'Försäljning omvänd skattskyldighet', '42': 'Övrig försäljning',
    '48': 'Ingående moms', '50': 'Import beskattningsunderlag',
    '60': 'Importmoms 25%', '61': 'Importmoms 12%', '62': 'Importmoms 6%'
};

const VATDetailView = ({ period, fiscalYearId }) => {

    const [vatData, setVatData] = useState({});
    const [totalVat, setTotalVat] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [boxAccounts, setBoxAccounts] = useState({}); // cached account breakdowns per box
    const [boxAccountsFetched, setBoxAccountsFetched] = useState(false);
    const { companyId } = useParams();

    useEffect(() => {
        const controller = new AbortController();

        const fetchVATCalculations = async () => {
            try {
                const getVATCalculationsApiUrl = getApiUrl(`${companyId}/reporting/vat-calculation`);
                const response = await axios.get(getVATCalculationsApiUrl, {
                    withCredentials: true,
                    signal: controller.signal,
                    params: {
                        start_date: period.start_date,
                        end_date: period.end_date,
                        fiscal_year_id: fiscalYearId,
                    },
                });

                if (response.data.success) {
                    setVatData(response.data.vatData);
                    setTotalVat(response.data.totalVat);
                } else {
                    throw new Error(response.data.message);
                }
            } catch (error) {
                if (error.name === 'CanceledError' || error.name === 'AbortError') return;
                console.error('Error fetching VAT calculations:', error);
                setError('Kunde inte hämta momsberäkningar');
            } finally {
                setLoading(false);
            }
        };

        if (period) {
            fetchVATCalculations();
        }

        return () => controller.abort();
    }, [companyId, period]);

    // Fetch account breakdowns for all boxes with data (once, after VAT data loads)
    useEffect(() => {
        if (boxAccountsFetched) return;
        if (!vatData || Object.keys(vatData).length === 0) return;

        const boxesWithData = Object.keys(vatData).filter(box => {
            const val = vatData[box];
            const amount = typeof val === 'object' ? val?.totalAmount : val;
            return amount && amount !== 0;
        });

        if (boxesWithData.length === 0) return;

        setBoxAccountsFetched(true);

        const fetchAll = async () => {
            const results = {};
            await Promise.all(boxesWithData.map(async (box) => {
                try {
                    const response = await axios.get(getApiUrl(`${companyId}/reporting/vat-box-transactions`), {
                        params: { box, start_date: period.start_date, end_date: period.end_date, fiscal_year_id: fiscalYearId },
                        withCredentials: true
                    });
                    if (response.data.success) {
                        // Group by account, then collect verifications under each
                        const grouped = {};
                        response.data.transactions.forEach(t => {
                            if (!grouped[t.account_number]) {
                                grouped[t.account_number] = {
                                    accountNumber: t.account_number,
                                    accountName: t.account_name,
                                    amount: 0,
                                    verifications: {}
                                };
                            }
                            const amt = parseFloat(t.amount);
                            const signedAmt = t.transaction_type === 'credit' ? amt : -amt;
                            grouped[t.account_number].amount += signedAmt;

                            const verKey = `${t.series}${t.number}`;
                            if (!grouped[t.account_number].verifications[verKey]) {
                                grouped[t.account_number].verifications[verKey] = {
                                    label: verKey,
                                    date: t.date,
                                    amount: 0
                                };
                            }
                            grouped[t.account_number].verifications[verKey].amount += signedAmt;
                        });
                        // Convert verifications object to array
                        const accountList = Object.values(grouped).map(acc => ({
                            ...acc,
                            verifications: Object.values(acc.verifications)
                        }));
                        results[box] = accountList;
                    }
                } catch (err) {
                    // Skip individual box errors
                }
            }));
            setBoxAccounts(results);
        };

        fetchAll();
    }, [vatData, boxAccountsFetched, companyId, period, fiscalYearId]);

    if (loading) return <p>Loading VAT details...</p>;
    if (error) return <p>{error}</p>;

    const formatAmount = (box) => new Intl.NumberFormat('sv-SE').format(vatData[box]?.totalAmount || 0);
    const hasAmount = (box) => (vatData[box]?.totalAmount || 0) !== 0;

    const renderBoxValue = (box) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Form.Control
                type="text"
                value={formatAmount(box)}
                readOnly
                style={{ flex: 1 }}
            />
            {hasAmount(box) && (
                <VatBoxPopover
                    title={`Ruta ${box} — ${BOX_LABELS[box] || ''}`}
                    accounts={boxAccounts[box] || []}
                    period={{ startDate: period.start_date, endDate: period.end_date }}
                    totalLabel="Totalt"
                />
            )}
        </div>
    );

    return (
        <Form>

            <Row style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                <Col md={6}>
                    <h5>A. Momspliktig försäljning eller uttag exklusive moms</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field05">
                                <Form.Label>05. Momspliktig försäljning som inte ingår i fält 06, 07 eller 08</Form.Label>
                                {renderBoxValue("05")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field06">
                                <Form.Label>06. Momspliktiga uttag</Form.Label>
                                {renderBoxValue("06")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field07">
                                <Form.Label>07. Beskattningsunderlag vid vinstmarginalbeskattning</Form.Label>
                                {renderBoxValue("07")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field08">
                                <Form.Label>08. Hyresinkomster vid frivillig beskattning</Form.Label>
                                {renderBoxValue("08")}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
                <Col md={6}>
                    <h5>B. Utgående moms på försäljning eller uttag i fält 05–08</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field10">
                                <Form.Label>10. Utgående moms 25 %</Form.Label>
                                {renderBoxValue("10")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field11">
                                <Form.Label>11. Utgående moms 12 %</Form.Label>
                                {renderBoxValue("11")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field12">
                                <Form.Label>12. Utgående moms 6 %</Form.Label>
                                {renderBoxValue("12")}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <Row style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                <Col md={6}>
                    <h5>C. Momspliktiga inköp vid omvänd betalningsskyldighet</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field20">
                                <Form.Label>20. Inköp av varor från annat EU-land</Form.Label>
                                {renderBoxValue("20")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field21">
                                <Form.Label>21. Inköp av tjänster från ett annat EU-land, enligt huvudregeln</Form.Label>
                                {renderBoxValue("21")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field22">
                                <Form.Label>22. Inköp av tjänster från länder utanför EU</Form.Label>
                                {renderBoxValue("22")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field23">
                                <Form.Label>23. Inköp av varor i Sverige som köparen är betalningsskyldig för</Form.Label>
                                {renderBoxValue("23")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field24">
                                <Form.Label>24. Övriga inköp av tjänster i Sverige som köparen är betalningsskyldig för</Form.Label>
                                {renderBoxValue("24")}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
                <Col md={6}>
                    <h5>D. Utgående moms på inköp i fält 20–24</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field30">
                                <Form.Label>30. Utgående moms 25 %</Form.Label>
                                {renderBoxValue("30")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field31">
                                <Form.Label>31. Utgående moms 12 %</Form.Label>
                                {renderBoxValue("31")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field32">
                                <Form.Label>32. Utgående moms 6 %</Form.Label>
                                {renderBoxValue("32")}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <Row style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                <Col md={6}>
                    <h5>H. Moms vid import</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field50">
                                <Form.Label>50. Beskattningsunderlag vid import</Form.Label>
                                {renderBoxValue("50")}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
                <Col md={6}>
                    <h5>I. Utgående moms på import i fält 50</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field60">
                                <Form.Label>60. Utgående moms 25 %</Form.Label>
                                {renderBoxValue("60")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field61">
                                <Form.Label>61. Utgående moms 12 %</Form.Label>
                                {renderBoxValue("61")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field62">
                                <Form.Label>62. Utgående moms 6 %</Form.Label>
                                {renderBoxValue("62")}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <Row style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                {/* Left side - Section E */}
                <Col md={6}>
                    <h5>E. Försäljning med mera som är undantagen från moms</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field35">
                                <Form.Label>35. Försäljning av varor till ett annat EU-land</Form.Label>
                                {renderBoxValue("35")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field36">
                                <Form.Label>36. Försäljning av varor utanför EU</Form.Label>
                                {renderBoxValue("36")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field37">
                                <Form.Label>37. Mellanmans inköp av varor vid trepartshandel</Form.Label>
                                {renderBoxValue("37")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field38">
                                <Form.Label>38. Mellanmans försäljning av varor vid trepartshandel</Form.Label>
                                {renderBoxValue("38")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field39">
                                <Form.Label>39. Försäljning av tjänster till en beskattningsbar person i ett annat EU-land, enligt huvudregeln</Form.Label>
                                {renderBoxValue("39")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field40">
                                <Form.Label>40. Övrig försäljning av tjänster som tillhandahållits utomlands</Form.Label>
                                {renderBoxValue("40")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field41">
                                <Form.Label>41. Försäljning när köparen är betalningsskyldig i Sverige</Form.Label>
                                {renderBoxValue("41")}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field42">
                                <Form.Label>42. Övrig försäljning med mera</Form.Label>
                                {renderBoxValue("42")}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>

                {/* Right side - Sections F and G */}
                <Col md={6}>
                    <h5>F. Ingående moms</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field48">
                                <Form.Label>48. Ingående moms att dra av</Form.Label>
                                {renderBoxValue("48")}
                            </Form.Group>
                        </Col>
                    </Row>

                    <h5>G. Moms att betala eller få tillbaka</h5>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Group controlId="field49">
                                <Form.Label>49. Moms att betala eller få tillbaka</Form.Label>
                                <Form.Control type="text" value={new Intl.NumberFormat('sv-SE').format(totalVat)} readOnly />
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Form>
    );
}

export default VATDetailView;
